import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useParams } from 'react-router-dom';
import "./App.css";
import "./responsive-improvements.css";
import { LanguageProvider } from './contexts/LanguageContext';
import GlobalSeo from './components/GlobalSeo';
import { Toaster } from './components/ui/sonner';
import Header from './components/HeaderPowerdecker';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import WhatsAppFloatingButton from './components/WhatsAppFloatingButton';
import HomePage from './pages/HomePage';
const PartnerLanding = lazy(() => import('./pages/PartnerPortal').then(m => ({ default: m.PartnerLanding })));
const PartnerVerify = lazy(() => import('./pages/PartnerPortal').then(m => ({ default: m.PartnerVerify })));
const PartnerDashboard = lazy(() => import('./pages/PartnerPortal').then(m => ({ default: m.PartnerDashboard })));
const PartnerReferralLanding = lazy(() => import('./pages/PartnerPortal').then(m => ({ default: m.PartnerReferralLanding })));
const DachdeckerDelmenhorst = lazy(() => import('./pages/cities/DachdeckerDelmenhorst'));
const DachdeckerBremen = lazy(() => import('./pages/cities/DachdeckerBremen'));

// Smart `/r/:code` wrapper: uppercase codes → Partner-Empfehlung,
// lowercase → bestehende Bestandskunden-Empfehlung. So bleibt das
// alte System unverändert und der vom User gewünschte URL-Pfad
// `/r/ABCD1234` funktioniert für Partner.
function ReferralRouteSwitch() {
  const { code } = useParams();
  if (code && code === code.toUpperCase() && /^[A-Z0-9]+$/.test(code)) {
    return <PartnerReferralLanding />;
  }
  return <ReferralSubmit />;
}

// Code-Splitting: Lazy-load aller nicht-kritischen Routen
const ProjektAnfrage = lazy(() => import('./pages/ProjektAnfrage'));
const PhotovoltaikMontage = lazy(() => import('./pages/services/PhotovoltaikMontage'));
const KomplettesPhotovoltaikAngebot = lazy(() => import('./pages/services/KomplettesPhotovoltaikAngebot'));
const KomplettInstallationen = lazy(() => import('./pages/services/KomplettInstallationen'));
const Dachreinigung = lazy(() => import('./pages/services/Dachreinigung'));
const InnendachDaemmung = lazy(() => import('./pages/services/InnendachDaemmung'));
const Dachsanierung = lazy(() => import('./pages/services/Dachsanierung'));
const BitumenFlachdach = lazy(() => import('./pages/services/BitumenFlachdach'));
const Fensterausbau = lazy(() => import('./pages/services/Fensterausbau'));
const FoerderserviceISFP = lazy(() => import('./pages/services/FoerderserviceISFP'));
const DachNeubau = lazy(() => import('./pages/services/DachNeubau'));
const UeberUns = lazy(() => import('./pages/UeberUns'));
const Impressum = lazy(() => import('./pages/Impressum'));
const Datenschutz = lazy(() => import('./pages/Datenschutz'));
const AGB = lazy(() => import('./pages/AGB'));
const CRMLogin = lazy(() => import('./pages/CRMLogin'));
const CRMDashboard = lazy(() => import('./pages/CRMDashboard'));
const CRMBeratung = lazy(() => import('./pages/CRMBeratung'));
const Danke = lazy(() => import('./pages/Danke'));
const Beratungstool = lazy(() => import('./pages/leistung/Beratungstool'));
const PVOfferSignaturePage = lazy(() => import('./pages/PVOfferSignaturePage'));
const DachOfferSignaturePage = lazy(() => import('./pages/DachOfferSignaturePage'));
const ExtOfferSignaturePage = lazy(() => import('./pages/ExtOfferSignaturePage'));
const SubSignPage = lazy(() => import('./pages/SubSignPage'));
const PortalApp = lazy(() => import('./pages/portal/PortalApp'));
const Mitarbeiterportal = lazy(() => import('./pages/Mitarbeiterportal'));
const DachsanierungDelmenhorst = lazy(() => import('./pages/cities/DachsanierungDelmenhorst'));
const DachsanierungBremen = lazy(() => import('./pages/cities/DachsanierungBremen'));
const DachsanierungOldenburg = lazy(() => import('./pages/cities/DachsanierungOldenburg'));
const DachsanierungStuhr = lazy(() => import('./pages/cities/DachsanierungStuhr'));
const DachsanierungGanderkesee = lazy(() => import('./pages/cities/DachsanierungGanderkesee'));
const DachsanierungWeyhe = lazy(() => import('./pages/cities/DachsanierungWeyhe'));
const SolaranlageBremen = lazy(() => import('./pages/cities/SolaranlageBremen'));
const DachsanierungPhotovoltaik = lazy(() => import('./pages/services/DachsanierungPhotovoltaik'));
const KfW358Landing = lazy(() => import('./pages/KfW358Landing'));
const DachsanierungMitPhotovoltaikPremium = lazy(() => import('./pages/services/DachsanierungMitPhotovoltaikPremium'));
const Waermepumpe = lazy(() => import('./pages/services/Waermepumpe'));
const PhotovoltaikWaermepumpe = lazy(() => import('./pages/services/PhotovoltaikWaermepumpe'));
const PVRechnerPublic = lazy(() => import('./pages/PVRechnerPublic'));
const PhotovoltaikAngebot = lazy(() => import('./pages/PhotovoltaikAngebot'));
const Referenzen = lazy(() => import('./pages/Referenzen'));
const WasKostetDachsanierung = lazy(() => import('./pages/ratgeber/WasKostetDachsanierung'));
const DachsanierungFoerderung = lazy(() => import('./pages/ratgeber/DachsanierungFoerderung'));
const PhotovoltaikLohntSich = lazy(() => import('./pages/ratgeber/PhotovoltaikLohntSich'));
const ReferralLanding = lazy(() => import('./pages/referral/ReferralLanding'));
const ReferralSubmit = lazy(() => import('./pages/referral/ReferralSubmit'));
const ReferralStatus = lazy(() => import('./pages/referral/ReferralStatus'));

// Layout wrapper that conditionally shows header/footer
function AppLayout({ children }) {
  const location = useLocation();
  const isPortalRoute = location.pathname.startsWith('/portal');
  const isMitarbeiterRoute = location.pathname.startsWith('/mitarbeiter');
  const isPartnerRoute = location.pathname.startsWith('/partner');
  const isReferralStatusRoute = /^\/r\/[^/]+\/status$/.test(location.pathname);
  // Partner-Referral-Landing (/r/UPPERCASE) → eigenes Dark-Glass Layout, kein Marketing-Header/Footer
  const isPartnerReferralLanding = /^\/r\/[A-Z0-9]+$/.test(location.pathname);
  // Google-Ads-Landingpage: eigenes Minimal-Layout ohne Marketing-Header/Footer
  const isAdsLanding = ['/photovoltaik-angebot', '/photovoltaik-rechner'].includes(location.pathname.replace(/\/+$/, ''));
  const isCRMRoute = isAdsLanding || location.pathname.startsWith('/crm') || location.pathname === '/login' || location.pathname.startsWith('/pv-angebot/sign/') || location.pathname.startsWith('/dach-angebot/sign/') || location.pathname.startsWith('/ext-sign/') || isPortalRoute || isMitarbeiterRoute || isPartnerRoute || isReferralStatusRoute || isPartnerReferralLanding;
  
  if (isCRMRoute) {
    return <>{children}</>;
  }
  
  return (
    <>
      <Header />
      <div className="pt-20 lg:pt-[120px]">
        {children}
      </div>
      <Footer />
      <WhatsAppFloatingButton />
      {/* Spacer damit Footer-Content nicht hinter mobiler Sticky-CTA-Leiste verschwindet */}
      <div className="md:hidden h-[88px]" aria-hidden="true" data-testid="mobile-cta-spacer" />
    </>
  );
}

// Minimaler Fallback für Suspense (kein Layout-Shift)
const RouteFallback = () => (
  <div style={{ minHeight: '60vh' }} />
);

function App() {
  return (
    <LanguageProvider>
      <Router>
        <div className="App">
          <GlobalSeo />
          <AppLayout>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/projekt-anfrage" element={<ProjektAnfrage />} />
            <Route path="/projekt-anfrage-meta" element={<ProjektAnfrage />} />
            <Route path="/projekt-anfrage/:source" element={<ProjektAnfrage />} />
            <Route path="/danke" element={<Danke />} />
            <Route path="/pv-angebot/sign/:token" element={<PVOfferSignaturePage />} />
            <Route path="/dach-angebot/sign/:token" element={<DachOfferSignaturePage />} />
            <Route path="/sub-sign/:token" element={<SubSignPage />} />
            <Route path="/ext-sign/:token" element={<ExtOfferSignaturePage />} />
            <Route path="/wp-sign/:token" element={<ExtOfferSignaturePage apiBase="/api/wp-offers/public/sign" />} />
            <Route path="/service/photovoltaik-montage" element={<PhotovoltaikMontage />} />
            <Route path="/service/komplettes-photovoltaik-angebot" element={<KomplettesPhotovoltaikAngebot />} />
            <Route path="/services/komplettes-pv-angebot" element={<KomplettesPhotovoltaikAngebot />} />
            <Route path="/service/komplett-installationen" element={<KomplettInstallationen />} />
            <Route path="/service/dachreinigung-beschichtung" element={<Dachreinigung />} />
            <Route path="/service/innendach-daemmung" element={<InnendachDaemmung />} />
            <Route path="/service/dachsanierung" element={<Dachsanierung />} />
            <Route path="/service/bitumen-flachdach" element={<BitumenFlachdach />} />
            <Route path="/service/fensterausbau" element={<Fensterausbau />} />
            <Route path="/service/foerderservice-isfp" element={<FoerderserviceISFP />} />
            <Route path="/service/dach-neubau" element={<DachNeubau />} />
            <Route path="/service/solaranlage" element={<PhotovoltaikMontage />} />
            <Route path="/service/dachsanierung-photovoltaik" element={<DachsanierungPhotovoltaik />} />
            <Route path="/dachsanierung-mit-photovoltaik" element={<DachsanierungMitPhotovoltaikPremium />} />
            <Route path="/waermepumpe" element={<Waermepumpe />} />
            <Route path="/photovoltaik-waermepumpe" element={<PhotovoltaikWaermepumpe />} />
            <Route path="/kfw-358-dachsanierung" element={<KfW358Landing />} />
            <Route path="/photovoltaik-rechner" element={<PVRechnerPublic />} />
            <Route path="/photovoltaik-angebot" element={<PhotovoltaikAngebot />} />
            <Route path="/dachsanierung-delmenhorst" element={<DachsanierungDelmenhorst />} />
            <Route path="/dachsanierung-bremen" element={<DachsanierungBremen />} />
            <Route path="/dachdecker-delmenhorst" element={<DachdeckerDelmenhorst />} />
            <Route path="/dachdecker-bremen" element={<DachdeckerBremen />} />
            <Route path="/dachsanierung-oldenburg" element={<DachsanierungOldenburg />} />
            <Route path="/dachsanierung-stuhr" element={<DachsanierungStuhr />} />
            <Route path="/dachsanierung-ganderkesee" element={<DachsanierungGanderkesee />} />
            <Route path="/dachsanierung-weyhe" element={<DachsanierungWeyhe />} />
            <Route path="/solaranlage-bremen" element={<SolaranlageBremen />} />
            <Route path="/referenzen" element={<Referenzen />} />
            <Route path="/ratgeber/was-kostet-dachsanierung" element={<WasKostetDachsanierung />} />
            <Route path="/ratgeber/dachsanierung-foerderung" element={<DachsanierungFoerderung />} />
            <Route path="/ratgeber/photovoltaik-lohnt-sich" element={<PhotovoltaikLohntSich />} />
            <Route path="/ueber-uns" element={<UeberUns />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/agb" element={<AGB />} />
            {/* CRM Routes (Internal) */}
            <Route path="/login" element={<CRMLogin />} />
            <Route path="/crm" element={<CRMDashboard />} />
            <Route path="/crm/beratung/:projectId" element={<CRMBeratung />} />
            <Route path="/leistung/beratungstool" element={<Beratungstool />} />
            {/* Customer Portal */}
            <Route path="/portal" element={<PortalApp />} />
            {/* Mitarbeiterportal (HR / Internes Team-Tool) */}
            <Route path="/mitarbeiter" element={<Mitarbeiterportal />} />
            <Route path="/mitarbeiter/login" element={<Mitarbeiterportal />} />
            {/* Empfehlungsprogramm */}
            <Route path="/empfehlen" element={<ReferralLanding />} />
            <Route path="/r/:code" element={<ReferralRouteSwitch />} />
            <Route path="/r/:code/status" element={<ReferralStatus />} />
            {/* Empfehlungs-/Partnerportal (eigenständige Schicht) */}
            <Route path="/partner" element={<PartnerLanding />} />
            <Route path="/partner/verify" element={<PartnerVerify />} />
            <Route path="/partner/dashboard" element={<PartnerDashboard />} />
            </Routes>
            </Suspense>
          </AppLayout>
          <Toaster />
          <CookieBanner />
        </div>
      </Router>
    </LanguageProvider>
  );
}

export default App;
